// src/components/ui/card/card.tsx

import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { CardProps } from '../../../types';
import { cn } from '../../../theme';

// Color mappings based on Prehistorik theme
const colorClasses = {
  stone: {
    bg: 'bg-stone-700',
    bgGradient: 'bg-gradient-to-br from-stone-800 to-stone-700',
    border: 'border-stone-600',
    text: 'text-stone-400',
    hover: 'hover:bg-stone-600 hover:border-stone-500'
  },
  moss: {
    bg: 'bg-moss-600',
    bgGradient: 'bg-gradient-to-br from-moss-700 to-moss-600',
    border: 'border-moss-600',
    text: 'text-moss-400',
    hover: 'hover:bg-moss-500 hover:border-moss-500'
  },
  sky: {
    bg: 'bg-sky-500',
    bgGradient: 'bg-gradient-to-br from-sky-500/20 to-sky-300/20',
    border: 'border-sky-500',
    text: 'text-sky-300',
    hover: 'hover:bg-sky-400 hover:border-sky-400'
  },
  sunset: {
    bg: 'bg-sunset-500',
    bgGradient: 'bg-gradient-to-br from-sunset-600/30 to-sunset-500/30',
    border: 'border-sunset-600',
    text: 'text-sunset-400',
    hover: 'hover:bg-sunset-400 hover:border-sunset-500'
  },
  berry: {
    bg: 'bg-berry-600',
    bgGradient: 'bg-gradient-to-br from-berry-700/20 to-berry-600/20',
    border: 'border-berry-700',
    text: 'text-berry-400',
    hover: 'hover:bg-berry-500 hover:border-berry-600'
  },
  cave: {
    bg: 'bg-cave-dark',
    bgGradient: 'bg-gradient-to-br from-cave-dark/50 to-cave-shadow/50',
    border: 'border-cave-dark',
    text: 'text-cave-crystal',
    hover: 'hover:bg-cave-shadow hover:border-cave-crystal'
  },
  ice: {
    bg: 'bg-ice-dark',
    bgGradient: 'bg-gradient-to-br from-ice-dark/30 to-ice-mid/30',
    border: 'border-ice-dark',
    text: 'text-ice-light',
    hover: 'hover:bg-ice-mid hover:border-ice-light'
  },
  volcano: {
    bg: 'bg-volcano-glow',
    bgGradient: 'bg-gradient-to-br from-volcano-dark to-volcano-glow',
    border: 'border-volcano-lava',
    text: 'text-volcano-ember',
    hover: 'hover:bg-volcano-hot hover:border-volcano-ember'
  },
  jungle: {
    bg: 'bg-jungle-mid',
    bgGradient: 'bg-gradient-to-br from-jungle-dark to-jungle-mid',
    border: 'border-jungle-dark',
    text: 'text-jungle-bright',
    hover: 'hover:bg-jungle-light hover:border-jungle-bright'
  },
  bone: {
    bg: 'bg-bone-200',
    bgGradient: 'bg-gradient-to-br from-bone-300 to-bone-200',
    border: 'border-bone-300',
    text: 'text-bone-200',
    hover: 'hover:bg-bone-100 hover:border-bone-200'
  }
};

// Common shadow styles
const shadows = {
  none: '',
  stone: 'shadow-stone',
  stoneLg: 'shadow-stone-lg',
  slab: 'shadow-slab',
  float: 'shadow-float',
  inner: 'shadow-inner'
};

// Common border styles
const borders = {
  none: '',
  thin: 'border-2',
  medium: 'border-3',
  thick: 'border-4',
  heavy: 'border-[5px]'
};

// Common rounded styles
const rounded = {
  none: '',
  sm: 'rounded',
  md: 'rounded-stone',
  lg: 'rounded-slab',
  full: 'rounded-full'
};

export const Card = React.forwardRef<HTMLDivElement, CardProps & HTMLMotionProps<"div">>(
  ({
    variant = 'gradient',
    colorTheme = 'stone',
    size = 'md',
    border = true,
    shadow = true,
    hover = true,
    clickable = false,
    onClick,
    header,
    footer,
    icon,
    title,
    subtitle,
    className,
    children,
    ...motionProps
  }, ref) => {
    const colorConfig = colorClasses[colorTheme as keyof typeof colorClasses] || colorClasses.stone;
    
    // Build class names based on props
    const cardClasses = cn(
      'h-full',
      variant === 'gradient' && colorConfig.bgGradient,
      variant === 'default' && colorConfig.bg,
      variant === 'outlined' && 'bg-transparent',
      variant === 'ghost' && 'bg-transparent',
      border && (variant === 'outlined' ? borders.medium : borders.thick),
      border && (variant === 'outlined' ? colorConfig.border : 'border-stone-900'),
      shadow && (variant === 'ghost' ? '' : shadows.stone),
      rounded.md,
      clickable && 'cursor-pointer',
      hover && clickable && 'transition-all hover:scale-[1.02]',
      hover && colorConfig.hover,
      'p-4',
      className
    );

    const content = (
      <>
        {(header || icon || title) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {icon && (
                <div className={cn('flex-shrink-0', colorConfig.text)}>
                  {icon}
                </div>
              )}
              <div className="flex-1 min-w-0">
                {title && (
                  <h3 className={cn('font-display text-lg truncate', colorConfig.text)}>
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p className="text-bone-200/60 text-sm truncate">{subtitle}</p>
                )}
              </div>
            </div>
            {header && <div className="flex-shrink-0">{header}</div>}
          </div>
        )}
        
        <div className="flex-1">{children}</div>
        
        {footer && (
          <div className="mt-3 pt-3 border-t border-stone-700">
            {footer}
          </div>
        )}
      </>
    );

    if (clickable || motionProps.animate || motionProps.whileHover) {
      return (
        <motion.div
          ref={ref}
          className={cardClasses}
          onClick={onClick}
          whileHover={hover ? { scale: 1.02 } : undefined}
          whileTap={clickable ? { scale: 0.98 } : undefined}
          {...motionProps}
        >
          {content}
        </motion.div>
      );
    }

    return (
      <div ref={ref} className={cardClasses} onClick={onClick}>
        {content}
      </div>
    );
  }
);

Card.displayName = 'Card';